<template>
  <div class="ticket card">
    <div class="ticket__journey-stations">
      <h2 class="ticket__journey-station">{{ originStation }}</h2>
      <svg class="ticket__icon">
        <use :xlink:href="`#${arrow}`" />
      </svg>
      <h2 class="ticket__journey-station">{{ destinationStation }}</h2>
    </div>
    <p class="ticket-travel__date">{{ formattedTravelDate }}</p>
    <div class="ticket-travel__time">
      <div class="ticket-travel__time-item">
        {{ formattedDepartureTime }}
      </div>
      <div class="ticket-travel__line-container">
        <div class="ticket-travel__line"></div>
        <div class="ticket-travel__duration">
          {{ formattedTravelTime }}
        </div>
        <div class="ticket-travel__line"></div>
      </div>
      <div class="ticket-travel__time-item">
        {{ formattedArrivalTime }}
      </div>
    </div>
    <div class="ticket__summary">
      <div class="pills">
        <span
          class="pill"
          v-if="totalPassengers > 0"
        >
          <svg class="pill__icon">
            <use :xlink:href="`#${pillPerson}`" />
          </svg>
          {{ formattedPassengers }}
        </span>
        <span
          class="pill"
          v-if="bookingSegment.required_products?.[0]?.name"
        >
          <svg class="pill__icon">
            <use :xlink:href="`#${pillTicket}`" />
          </svg>
          {{ bookingSegment.required_products[0].name }}
        </span>
        <span
          class="pill"
          v-if="seatNumber !== '--'"
        >
          <svg class="pill__icon">
            <use :xlink:href="`#${pillSeat}`" />
          </svg>
          {{ seatNumber }}
        </span>
      </div>
    </div>
    <div
      class="ticket__details"
      :class="{ 'ticket__details--expanded': isExpanded }"
    >
      <div
        class="ticket__details-item"
        v-if="customerName"
      >
        <div>{{ $t('myPage.ticket.name') }}</div>
        <div>{{ customerName }}</div>
      </div>
      <div class="ticket__details-item">
        <div>{{ $t('myPage.ticket.passengers') }}</div>
        <div class="ticket__passenger-list">
          {{ formattedPassengers }}
        </div>
      </div>
      <div class="ticket__details-item">
        <div>{{ $t('myPage.ticket.ticketTyp') }}</div>
        <div>{{ bookingSegment.required_products?.[0]?.name || '--' }}</div>
      </div>
      <div
        class="ticket__details-item"
        v-if="hasSeats"
      >
        <div>{{ $t('myPage.ticket.seat') }}</div>
        <div class="ticket__seat-list">
          <div
            v-for="(seat, index) in allSeats"
            :key="index"
          >
            {{ seat }}
          </div>
        </div>
      </div>
      <div
        class="ticket__details-item"
        v-if="filteredAdditionalProducts.length"
      >
        <div>{{ $t('myPage.ticket.equipment') }}</div>
        <div>
          <div
            v-for="(product, index) in filteredAdditionalProducts"
            :key="index"
          >
            {{ product.name }}
          </div>
        </div>
      </div>
      <div
        class="ticket__details-item"
        v-if="discountCards.length"
      >
        <div>{{ $t('myPage.ticket.discount') }}</div>
        <div>
          <div
            v-for="(card, index) in discountCards"
            :key="index"
          >
            {{ card }}
          </div>
        </div>
      </div>
      <div class="ticket__details-item">
        <div>{{ $t('myPage.ticket.totalPrice') }}</div>
        <div>{{ segmentPrice }} kr</div>
      </div>
    </div>
    <div
      class="ticket__expand"
      :class="{ open: isExpanded }"
    >
      <button
        class="expand-button button button--rounded"
        @click="toggleExpand"
      >
        <svg
          class="ticket__icon"
          :class="{ 'ticket__icon--expanded': isExpanded }"
        >
          <use :xlink:href="`#${chevron}`" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, computed, ref, PropType } from 'vue'
  import { useI18n } from 'vue-i18n'
  import arrow from '@/assets/icons/arrow.svg'
  import chevron from '@/assets/icons/chevron.svg'
  import { formatDate, formatTime } from '@/utils/dateUtils'
  import { Passenger } from '@/types/passengerTypes'
  import { Station } from '@/types/stationTypes'
  import pillPerson from '@/assets/icons/pillPerson.svg'
  import pillTicket from '@/assets/icons/pillTicket.svg'
  import pillSeat from '@/assets/icons/pillSeat.svg'

  interface BookingSegment {
    id: string
    uuid: string
    departure_station: Station
    arrival_station: Station
    travel_date: string
    sequence_number: number
    has_new_main_products: boolean
    validity_service: string
    comfort_zone_code: string
    validity_start_date: string
    booking_journey_segments: Array<{
      id: string
      departure_station: Station
      arrival_station: Station
      service_name: string
      departure_date_time: string
      arrival_date_time: string
    }>
    required_products: Array<{
      id?: string
      name: string
      passenger_id: string
      passenger_type?: string
      product_family?: string
      price?: number
      total_price_to_be_paid?: number
      original_price?: number
      tariff_code?: string
      seat?: {
        carriage: string
        number: string
        reversed: boolean
      }
      discounts?: Array<{
        name: string
        code: string
        amount: number
        percentage: number
        type: string
      }>
      cancelled?: boolean
    }>
    client_ref?: string
    additional_products?: Array<{
      name: string
      code: string
      tariff_code: string
      type: string
    }>
    total_price?: number
  }

  interface AdditionalProduct {
    name: string
    code: string
    type: string
    price: number
  }

  export default defineComponent({
    name: 'Ticket',
    props: {
      bookingSegment: {
        type: Object as () => BookingSegment,
        required: true,
      },
      customerName: {
        type: String,
        default: '',
      },
      passengers: {
        type: Array as () => Passenger[],
        required: true,
      },
      totalPricePaid: {
        type: Number,
        required: true,
      },
      segmentPrice: {
        type: Number,
        required: true,
      },
      vouchers: {
        type: Array as () => Array<{ amount: number; currency: string }>,
        default: () => [],
      },
      qrCodes: {
        type: Array as () => Array<{ url: string }>,
        default: () => [],
      },
      isTransferSegment: {
        type: Boolean,
        default: false,
      },

      transferInfo: {
        type: Object as PropType<{
          location: string
          duration: number
          arrivalTime: string
          departureTime: string
        } | null>,
        default: null,
      },
    },
    setup(props) {
      const i18n = useI18n()
      const isExpanded = ref(false)

      const toggleExpand = () => {
        isExpanded.value = !isExpanded.value
      }

      const originStation = computed(
        () => props.bookingSegment.departure_station.name
      )
      const destinationStation = computed(
        () => props.bookingSegment.arrival_station.name
      )

      const departureDateTime = computed(() => {
        const segments = props.bookingSegment.booking_journey_segments
        return segments?.[0]?.departure_date_time
      })

      const arrivalDateTime = computed(() => {
        const segments = props.bookingSegment.booking_journey_segments
        return segments?.[0]?.arrival_date_time
      })

      const formattedTravelDate = computed(() =>
        formatDate(departureDateTime.value)
      )
      const formattedDepartureTime = computed(() =>
        formatTime(departureDateTime.value)
      )
      const formattedArrivalTime = computed(() =>
        formatTime(arrivalDateTime.value)
      )

      const formattedTravelTime = computed(() => {
        if (!departureDateTime.value || !arrivalDateTime.value) return '--'
        const departure = new Date(departureDateTime.value)
        const arrival = new Date(arrivalDateTime.value)
        const diffInMinutes =
          (arrival.getTime() - departure.getTime()) / (1000 * 60)

        const hours = Math.floor(diffInMinutes / 60)
        const minutes = Math.floor(diffInMinutes % 60)

        if (hours === 0) {
          return `${minutes}min`
        }

        return `${hours}t ${minutes}min`
      })

      const passengerTypeMap: Record<string, string> = {
        ADT: 'adult',
        CHD: 'child',
        COM: 'companion',
        CON: 'conscript',
        SEN: 'senior',
        STU: 'student',
        ST: 'student',
        YOU: 'youth',
        INF: 'infant',
      }

      const passengerCounts = computed(() => {
        const counts: Record<string, number> = {}
        props.passengers.forEach((passenger) => {
          const type = passenger.type
          if (!counts[type]) {
            counts[type] = 0
          }
          counts[type]++
        })
        return counts
      })

      const formattedPassengers = computed(() => {
        if (!props.passengers?.length) return ''

        return Object.entries(passengerCounts.value)
          .map(([type, count]) => {
            const translationKey = `common.passengerCount.${passengerTypeMap[type]}`
            const translatedType = i18n
              .t(translationKey, { count })
              .replace(/^\d+\s/, '') // Remove leading number and space
            return `${count} x ${translatedType}`
          })
          .join(', ')
      })

      const totalPassengers = computed(() => props.passengers.length)

      const allSeats = computed(() => {
        return (
          props.bookingSegment.required_products
            ?.map((product) => product.seat?.number)
            .filter(Boolean) || []
        )
      })

      const hasSeats = computed(() => allSeats.value.length > 0)

      const seatNumber = computed(() => {
        const seat = props.bookingSegment.required_products?.[0]?.seat
        if (!seat) return '--'
        return seat.number
      })

      const discountCards = computed(() => {
        return props.passengers
          .filter(
            (passenger) =>
              passenger.discount_cards && passenger.discount_cards.length > 0
          )
          .flatMap((passenger) => passenger.discount_cards || [])
          .map((card) => card.name)
          .filter(Boolean)
      })

      const voucherAmount = computed(() => {
        return props.vouchers.reduce((sum, voucher) => sum + voucher.amount, 0)
      })

      const filteredAdditionalProducts = computed(() => {
        return (
          props.bookingSegment.additional_products?.filter(
            (product) => product.type !== 'SF'
          ) || []
        )
      })

      return {
        arrow,
        chevron,
        pillPerson,
        pillTicket,
        pillSeat,
        originStation,
        destinationStation,
        formattedTravelDate,
        formattedDepartureTime,
        formattedArrivalTime,
        formattedTravelTime,
        seatNumber,
        formattedPassengers,
        voucherAmount,
        passengerCounts,
        allSeats,
        hasSeats,
        passengerTypeMap,
        totalPassengers,
        isExpanded,
        toggleExpand,
        discountCards,
        filteredAdditionalProducts,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .ticket {
    padding: rem(18) 18px rem(32);
    position: relative;
    margin-bottom: rem(16);

    &-travel {
      &__date {
        margin-top: 0;
        margin-bottom: rem(18);
      }

      &__time {
        margin-top: rem(18);
        margin-bottom: rem(18);
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-bottom: rem(18);
        border-bottom: 1px dashed $clr-primary-100;

        &-item {
          @include title-medium;

          @media screen and (min-width: $breakpoint-xs) {
            @include title-large;
          }
        }
      }

      &__line-container {
        display: flex;
        align-items: center;
        flex-grow: 1;
        margin: 4px 0 0;
      }

      &__line {
        border-bottom: 1px solid $clr-primary-100;
        flex-grow: 1;
        margin: 4px 8px 0;
      }

      &__duration {
        @include body-large;
        color: $clr-text;
        white-space: nowrap;
      }
    }

    &__summary {
      margin-bottom: rem(16);
    }

    &__details {
      display: flex;
      flex-direction: column;
      gap: 6px;
      margin-top: rem(32);
      margin-bottom: rem(8);

      @media screen and (max-width: $breakpoint-sm) {
        display: none;
        margin: 0;
        height: 0;
        overflow: hidden;

        &--expanded {
          display: flex;
          margin-top: rem(32);
          margin-bottom: rem(8);
          height: auto;
        }
      }

      &-item {
        display: flex;
        justify-content: space-between;
        text-transform: capitalize;
        gap: 18px;
        padding-top: 6px;
        padding-bottom: 12px;
        color: $clr-text;
        border-bottom: 1px solid $clr-primary-100;

        &--equipment {
          flex-direction: column;
          gap: 0.25rem;

          > div:last-child {
            margin-top: 0.25rem;
          }
        }
      }
    }

    &__expand {
      display: none;

      @media screen and (max-width: $breakpoint-sm) {
        display: flex;
        justify-content: center;
        position: absolute;
        right: 0;
        bottom: -4px;
        left: 0;
        height: 0;

        .expand-button {
          transform: translateY(-50%);

          svg {
            color: $clr-primary;
          }
        }

        &.open {
          .expand-button svg {
            transform: rotate(0deg);
          }
        }
      }
    }

    &__journey-stations {
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: rem(18);
    }

    &__journey-station {
      @include title-large;
      margin-top: 0;
      margin-bottom: 0;

      @media screen and (min-width: $breakpoint-xs) {
        @include headline-small;
      }
    }

    &__icon {
      width: 16px;
      height: 16px;
      transform: rotate(180deg);
      color: $clr-secondary;
      transition: transform 0.3s ease;

      &--expanded {
        transform: rotate(0deg);
      }

      @media screen and (min-width: $breakpoint-xs) {
        width: 20px;
        height: 20px;
      }
    }
  }

  .pills {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .pill {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    background-color: var(--background-secondary);
    border-radius: 1rem;
    font-size: 0.875rem;

    &__icon {
      width: 16px;
      height: 16px;
    }
  }

  .expand-button {
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $clr-white;
    border: 1px solid $clr-primary-100;
    border-radius: 50%;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: $clr-primary-50;
    }
  }

  .ticket__passenger-list,
  .ticket__seat-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
</style>
